/**
 * recuDeCopie — ce que le reçu d'une copie de forme (`copy_format`, #1869) dit
 * en plus du reçu commun (`recuDEcritureDeForme.ts`) : le modèle, le passage
 * dont la forme de texte a été retenue, et tout ce que la copie a laissé —
 * compté, nommé, avec sa raison.
 *
 * Module pur. Les règles qui font laisser un réglage sont dites dans l'en-tête
 * de `copieDeForme.ts` ; ici, seulement leur mise en mots pour l'agent.
 */

import {
  CANVAS_FORMAT_COPY_SHOWN_RUNS_MAX,
  CANVAS_FORMAT_DIFFERENCE_EXAMPLES_MAX,
  CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX,
  CANVAS_PARAGRAPH_BORDER_SIDES,
  CanvasFormatNotCopiedReasonSchema,
  type CanvasFormatNotCopiedReason,
  type CanvasFormatWriteReceipt,
} from '@hector/shared-types';
import { copieUneFormeDeTexte, type FormeCopiee, type FormeDeParagrapheGardee } from './copieDeForme';
import { voisinsSurLaPage, type FormeDeParagraphe } from '@hector/docx-forme';
import { paragraphePourLAgent } from './formePourLAgent';
import { memeCote, type CibleCopiee } from './verificationDeCopie';
import type { RelectureDeLActe } from './verificationDeForme';

/** Les faits propres à une copie de forme, tels que l'exécuteur les a relevés. */
export interface FaitsDeLaCopie {
  /** L'identifiant du paragraphe modèle. */
  modele: string;
  /** Ce que la copie a repris du modèle : les valeurs demandées, et ce qui n'est copié sur aucune cible. */
  forme: FormeCopiee;
  /** Les cibles entièrement écrites, ou qui n'avaient rien à recevoir : celles que la relecture juge. */
  cibles: readonly CibleCopiee[];
  /** Les cibles dont les retraits n'ont pas été copiés (liste), dans l'ordre du document. */
  retraitsGardes: readonly string[];
  /** Les cibles dont le style nommé n'a pas été changé (il porte leur numérotation). */
  styleGarde: readonly string[];
  /** Les cibles dont la forme de texte n'a pas été écrite (révision en attente sur leur texte). */
  texteLaisse: readonly string[];
  /** Les côtés `between` et `bar` que le modèle ou une cible porte à la main. */
  cotesHorsCopie: ReadonlyArray<'between' | 'bar'>;
  /** Les éléments de forme de caractère que des cibles portent et que l'éditeur ne sait pas retirer, avec le nombre de cibles. */
  texteNonRetirable: ReadonlyArray<{ element: string; paragraphs: number }>;
  /** La forme de paragraphe non reprise que des cibles gardent et que le modèle ne porte pas, avec le nombre de cibles. */
  paragrapheGarde: ReadonlyArray<{ what: FormeDeParagrapheGardee; paragraphs: number }>;
}

type CopieAuRecu = NonNullable<CanvasFormatWriteReceipt['copy']>;

const RAISON_EN_CLAIR: Readonly<Record<CanvasFormatNotCopiedReason, string>> = {
  unreadable_on_source: 'cannot be read on the model paragraph (a theme colour, a line spacing fixed in points, a superscript size, a border line without thickness, a style missing from the file)',
  source_has_no_text: 'the model paragraph has no text, so it shows no text formatting',
  source_has_no_named_style: 'the model paragraph has no named style the file defines',
  style_numbers_paragraphs: 'the named style of the model paragraph carries list numbering, and setting it would number the targets',
  border_line_type_not_measured: 'a side of the border of the model is of a line type other than single, double, dotted or dashed, which the editor is not known to write faithfully',
};

/** Des paragraphes nommés jusqu'à la borne du contrat, comptés au-delà ; `undefined` s'il n'y en a pas. */
function groupe<Raison extends string>(reason: Raison, nodeIds: readonly string[]): { reason: Raison; nodeIds: string[]; paragraphsNotListed?: number } | undefined {
  if (nodeIds.length === 0) return undefined;
  const nonNommes = nodeIds.length - CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX;
  return { reason, nodeIds: nodeIds.slice(0, CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX), ...(nonNommes > 0 ? { paragraphsNotListed: nonNommes } : {}) };
}

const dire = (nodeIds: readonly string[]): string => {
  const nonNommes = nodeIds.length - CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX;
  return `${nodeIds.slice(0, CANVAS_FORMAT_NAMED_PARAGRAPHS_MAX).join(', ')}${nonNommes > 0 ? ` and ${nonNommes} more` : ''}`;
};

/**
 * Le nombre de paragraphes hors tableau qui, dans le fichier relu (corps et cellules, dans l'ordre du document), se suivent en
 * montrant la bordure copiée, dans chaque suite qui compte une cible : la page
 * peint une telle suite comme UN cadre, sans trait entre ses paragraphes
 * (`reglagesDeParagraphe.ts`, « La bordure »). Compté sur le fichier relu, pas
 * sur les écritures : le modèle ou un voisin déjà encadré entre dans la suite.
 */
function voisinsEncadres(copie: FaitsDeLaCopie, relecture: RelectureDeLActe): number {
  const { traits } = copie.forme;
  if (!('acte' in relecture) || traits === undefined || CANVAS_PARAGRAPH_BORDER_SIDES.every((cote) => traits[cote] === null)) return 0;
  const ecrites = new Set(copie.cibles.map((cible) => cible.nodeId));
  const montreLaBordure = (lu: FormeDeParagraphe): boolean => CANVAS_PARAGRAPH_BORDER_SIDES.every((cote) => {
    const trait = lu.traitsDeBordure[cote];
    return trait.lisible && memeCote(trait.valeur, traits[cote]);
  });
  let total = 0;
  let suite: FormeDeParagraphe[] = [];
  const clore = (): void => {
    if (suite.length >= 2 && suite.some((lu) => lu.nodeId !== null && ecrites.has(lu.nodeId))) total += suite.length;
    suite = [];
  };
  // Le cadre commun n'est mesuré qu'entre voisins sur la page : un paragraphe de cellule n'en a pas.
  for (const voisins of voisinsSurLaPage(relecture.acte)) {
    for (const lu of voisins) {
      if (montreLaBordure(lu)) suite.push(lu);
      else clore();
    }
    clore();
  }
  return total;
}

/**
 * Ce que l'effet du reçu dit de plus pour une copie, sans en faire un écart :
 * la forme de paragraphe que des cibles gardent, et les voisins peints comme un
 * seul cadre. La copie a fait ce qu'elle dit faire ; l'avocat doit seulement
 * savoir ce que la page montre.
 */
export function ditDeLaCopie(copy: CopieAuRecu): string {
  const gardes = copy.paragraphFormattingKept?.map(({ what, paragraphs }) => `${what} on ${paragraphs}`).join(', ');
  return `${gardes === undefined ? '' : ` Some targets keep hand-set paragraph formatting the model does not have (format.copy.paragraphFormattingKept: ${gardes}): a copy does not remove it — say so if the lawyer expects an identical look.`}`
    + `${copy.borderedNeighbours === undefined ? '' : ` ${copy.borderedNeighbours} paragraphs in a row now carry the same border (format.copy.borderedNeighbours): the page draws each such row as ONE frame, with no line between its paragraphs.`}`;
}

/**
 * Le champ `format.copy` du reçu : le modèle, le passage retenu, et ce qui a été laissé.
 *
 * @param relecture Le fichier relu après l'écriture : il dit ce que montre le texte d'une cible dont la forme de texte n'a pas été écrite.
 */
export function copieAuRecu(copie: FaitsDeLaCopie, relecture: RelectureDeLActe): CopieAuRecu {
  const { forme } = copie;
  const [premierNonCopie, ...autresNonCopies] = forme.nonCopies;
  const [premierCote, ...autresCotes] = copie.cotesHorsCopie;
  const [premierElement, ...autresElements] = copieUneFormeDeTexte(forme) ? forme.texteNonCopie : [];
  const [premierNonRetire, ...autresNonRetires] = copie.texteNonRetirable;
  const [premierGarde, ...autresGardes] = copie.paragrapheGarde;
  const indentsKept = groupe('list_numbering', copie.retraitsGardes);
  const styleKept = groupe('list_numbering', copie.styleGarde);
  const texteLaisse = groupe('pending_tracked_change', copie.texteLaisse);
  // Ce que le texte laissé montre : chaque passage relu, ses réglages rangés par origine — le style posé y paraît sous « style ».
  const relus = 'acte' in relecture ? relecture.acte.paragraphes : [];
  const montres = copie.texteLaisse.slice(0, CANVAS_FORMAT_DIFFERENCE_EXAMPLES_MAX).flatMap((nodeId) => {
    const relu = relus.find((lu) => lu.nodeId === nodeId);
    return relu === undefined ? [] : [{ nodeId, runs: paragraphePourLAgent({ ...relu, nodeId }).runs.slice(0, CANVAS_FORMAT_COPY_SHOWN_RUNS_MAX) }];
  });
  const [premierMontre, ...autresMontres] = montres;
  const voisins = voisinsEncadres(copie, relecture);
  return {
    from: copie.modele,
    // Le passage retenu n'est dit que si une forme de texte a été copiée.
    ...(forme.passageRetenu === undefined || forme.passageRetenu.texte === '' || !copieUneFormeDeTexte(forme)
      ? {}
      : { textFormFrom: { text: forme.passageRetenu.texte, passagesInSource: forme.passageRetenu.passages } }),
    ...(premierNonCopie === undefined ? {} : { notCopied: [premierNonCopie, ...autresNonCopies] }),
    ...(premierCote === undefined ? {} : { borderSidesNotCopied: [premierCote, ...autresCotes] }),
    ...(indentsKept === undefined ? {} : { indentsKept }),
    ...(styleKept === undefined ? {} : { styleKept }),
    ...(texteLaisse === undefined ? {} : { textFormattingLeft: { ...texteLaisse, ...(premierMontre === undefined ? {} : { shown: [premierMontre, ...autresMontres] }) } }),
    ...(premierElement === undefined ? {} : { textFormattingNotCopied: [premierElement, ...autresElements] }),
    ...(premierNonRetire === undefined ? {} : { textFormattingNotRemoved: [premierNonRetire, ...autresNonRetires] }),
    ...(premierGarde === undefined ? {} : { paragraphFormattingKept: [premierGarde, ...autresGardes] }),
    ...(voisins < 2 ? {} : { borderedNeighbours: voisins }),
  };
}

/**
 * La vérification propre à une copie : tout ce que le modèle porte a-t-il été
 * donné à chaque cible. Une cible sautée, ou écrite sur une révision de forme
 * de paragraphe en attente, est dite par les vérifications communes du reçu.
 */
export function verificationsDeLaCopie(copie: FaitsDeLaCopie): Array<{ name: string; passed: boolean; message: string }> {
  const { forme } = copie;
  const toutCopie = forme.nonCopies.length === 0 && (!copieUneFormeDeTexte(forme) || forme.texteNonCopie.length === 0)
    && copie.retraitsGardes.length === 0 && copie.styleGarde.length === 0 && copie.texteLaisse.length === 0
    && copie.cotesHorsCopie.length === 0 && copie.texteNonRetirable.length === 0;
  return [
    {
      name: 'format_copy_gave_every_target_the_whole_form',
      passed: toutCopie,
      message: 'A copy must give every target every setting of its model: none unreadable on the model, none kept or left on a target for its list numbering or a pending tracked change, no character formatting left behind.',
    },
  ];
}

/** Les erreurs qu'une copie ajoute au reçu : chaque chose laissée, dite une fois, avec ce que l'agent doit en faire. */
export function erreursDeLaCopie(copie: FaitsDeLaCopie): Array<{ code: string; message: string }> {
  const { forme } = copie;
  const nonCopiesParRaison = CanvasFormatNotCopiedReasonSchema.options.flatMap((raison) => {
    const reglages = forme.nonCopies.filter((nonCopie) => nonCopie.reason === raison).map((nonCopie) => nonCopie.setting);
    return reglages.length === 0 ? [] : [`${reglages.join(', ')} — ${RAISON_EN_CLAIR[raison]}`];
  });
  const elementsNonCopies = copieUneFormeDeTexte(forme) ? forme.texteNonCopie : [];
  return [
    ...(nonCopiesParRaison.length === 0 ? [] : [{
      code: 'FORMAT_COPY_SETTINGS_NOT_COPIED',
      message: `copy_format did not copy these settings from model paragraph ${copie.modele} (format.copy.notCopied): ${nonCopiesParRaison.join('; ')}. `
        + 'A value that cannot be read is never guessed: the targets keep their own value for these settings. Resending would not copy them; '
        + 'tell the lawyer which settings were not copied, and set them with format_text or format_paragraphs if the lawyer gives a value.',
    }]),
    ...(elementsNonCopies.length === 0 ? [] : [{
      code: 'FORMAT_COPY_TEXT_FORMATTING_NOT_COPIED',
      message: `Model paragraph ${copie.modele} carries, set by hand, character formatting outside the seven text settings (format.copy.textFormattingNotCopied, as file element names: ${elementsNonCopies.join(', ')}). `
        + 'copy_format cannot read its value, so it was copied onto no target — and removed from the targets that carried the same. The targets therefore do not show it. '
        + 'Resending would not copy it; tell the lawyer which formatting of the model was not reproduced.',
    }]),
    ...(copie.texteNonRetirable.length === 0 ? [] : [{
      code: 'FORMAT_COPY_TEXT_FORMATTING_NOT_REMOVED',
      message: `Some targets carry, set by hand, character formatting the editor cannot remove (format.copy.textFormattingNotRemoved: ${copie.texteNonRetirable.map(({ element, paragraphs }) => `${element} on ${paragraphs} paragraph(s)`).join(', ')}). `
        + 'It stays on them, so their text does not show exactly the form of the model. Resending would not remove it; tell the lawyer.',
    }]),
    ...(copie.retraitsGardes.length === 0 ? [] : [{
      code: 'FORMAT_COPY_INDENTS_KEPT',
      message: `copy_format did not copy the indents onto ${copie.retraitsGardes.length} target paragraph(s) (format.copy.indentsKept): ${dire(copie.retraitsGardes)}. `
        + 'The model or the target is a numbered or bulleted paragraph, whose indents belong to its list: a copy keeps lists as they are. '
        + 'Everything else was copied onto them. Resending would leave them the same way; tell the lawyer these paragraphs keep their indents.',
    }]),
    ...(copie.styleGarde.length === 0 ? [] : [{
      code: 'FORMAT_COPY_STYLE_KEPT',
      message: `copy_format did not change the named style of ${copie.styleGarde.length} target paragraph(s) (format.copy.styleKept): ${dire(copie.styleGarde)}. `
        + 'Their own style carries their list numbering, and replacing it would remove the numbering. The form of the model was written on them directly instead. '
        + 'Resending would leave them the same way; tell the lawyer these paragraphs keep their style.',
    }]),
    ...(copie.texteLaisse.length === 0 ? [] : [{
      code: 'FORMAT_TEXT_FORMATTING_LEFT_AS_IS',
      message: `copy_format copied the named style and the paragraph formatting onto ${copie.texteLaisse.length} target paragraph(s) and wrote NO text formatting on their text (format.copy.textFormattingLeft): ${dire(copie.texteLaisse)}. `
        + 'Their text carries a pending tracked change (text formatting, or a move) the editor refuses to write over; the pending change itself is untouched. '
        + 'format.copy.textFormattingLeft.shown gives what their text shows now, passage by passage: under "style", what the named style just set gives; under "direct", what they still carry by hand — not the text form of the model. '
        + 'Resending would leave their text the same way; tell the lawyer what these paragraphs show, and to accept or reject their pending changes first, then copy the form again.',
    }]),
    ...(copie.cotesHorsCopie.length === 0 ? [] : [{
      code: 'FORMAT_COPY_BORDER_SIDES_NOT_COPIED',
      message: `copy_format copies the four sides of a paragraph border (top, left, bottom, right). The model or a target also carries, set by hand, the side(s) ${copie.cotesHorsCopie.join(', ')} `
        + '(format.copy.borderSidesNotCopied), which were neither copied nor removed: they stay as they were on each paragraph. Tell the lawyer.',
    }]),
  ];
}
